/**
 * 暗黑模式 Composable
 * 
 * 提供暗黑模式切换功能，状态持久化到 localStorage
 */

import { ref, watch, onMounted } from 'vue';

const STORAGE_KEY = 'tenant-console-dark-mode';

// 全局状态，所有组件共享
const isDark = ref(false);

/**
 * 暗黑模式 Hook
 */
export function useDarkMode() {
  /**
   * 初始化暗黑模式
   */
  const initDarkMode = () => {
    // 从 localStorage 读取用户偏好
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (stored !== null) {
      // 使用用户保存的偏好
      isDark.value = stored === 'true';
    } else {
      // 首次访问，使用系统偏好
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // 应用暗黑模式
    applyDarkMode(isDark.value);
  };

  /**
   * 应用暗黑模式到 DOM
   */
  const applyDarkMode = (dark: boolean) => {
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  /**
   * 切换暗黑模式
   */
  const toggleDarkMode = () => {
    isDark.value = !isDark.value;
    applyDarkMode(isDark.value);
    localStorage.setItem(STORAGE_KEY, String(isDark.value));
  };

  /**
   * 设置暗黑模式
   */
  const setDarkMode = (dark: boolean) => {
    isDark.value = dark;
    applyDarkMode(dark);
    localStorage.setItem(STORAGE_KEY, String(dark));
  };

  // 监听系统主题变化（仅当用户未手动设置时）
  onMounted(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // 只有当用户没有手动设置过偏好时，才跟随系统
      if (localStorage.getItem(STORAGE_KEY) === null) {
        isDark.value = e.matches;
        applyDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // 清理函数
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  });

  return {
    isDark,
    toggleDarkMode,
    setDarkMode,
    initDarkMode,
  };
}
